<template>
  <div class="splash" :class="{ 'is-leaving': leaving }">
    <!-- 数据贯通意象：节点网络 + 流动连线 -->
    <svg class="stage" viewBox="0 0 1200 640" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <linearGradient id="splash-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stop-color="#2997ff" stop-opacity="0" />
          <stop offset="45%" stop-color="#2997ff" stop-opacity="0.85" />
          <stop offset="100%" stop-color="#7bdcff" stop-opacity="0" />
        </linearGradient>
        <radialGradient id="splash-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#2997ff" stop-opacity="0.30" />
          <stop offset="100%" stop-color="#2997ff" stop-opacity="0" />
        </radialGradient>
      </defs>

      <circle cx="600" cy="320" r="300" fill="url(#splash-glow)" class="glow" />

      <g class="links">
        <line
          v-for="(l, i) in links"
          :key="'l' + i"
          :x1="l.a.x" :y1="l.a.y" :x2="l.b.x" :y2="l.b.y"
          stroke="url(#splash-line)"
          stroke-width="1.2"
          class="link"
          :style="{ animationDelay: l.delay + 'ms' }"
        />
      </g>

      <g class="nodes">
        <g v-for="(n, i) in nodes" :key="'n' + i" class="node"
           :style="{ animationDelay: n.delay + 'ms' }">
          <circle :cx="n.x" :cy="n.y" :r="n.r + 6" fill="#2997ff" opacity="0.12" />
          <circle :cx="n.x" :cy="n.y" :r="n.r" fill="#e8f2ff" opacity="0.92" />
        </g>
      </g>
    </svg>

    <div class="content">
      <h1 class="title">数据贯通成熟度评价</h1>
      <p class="eyebrow">企业级 / 项目级 · 三方十类</p>
      <p class="theme">
        旨在为打造高效数据管理与维护环境，<br />
        提升数据贯通效率，明确提升路径。
      </p>

      <div class="progress" role="progressbar" :aria-valuenow="100">
        <i></i>
      </div>
    </div>

    <button class="skip" type="button" @click="finish">跳过</button>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const emit = defineEmits(['finish'])

// 自动结束时长与淡出时长（毫秒）
const DURATION = 4000
const FADE = 700

const leaving = ref(false)
let autoTimer = null
let fadeTimer = null

// 节点与连线在挂载时生成一次：避免手写大量坐标，也保证刷新后版式稳定
const nodes = ref([])
const links = ref([])

function buildStage () {
  // 三圈同心布局，隐喻「三方 → 十类 → 六能力域」的层级贯通
  const rings = [
    { count: 6, radius: 250, r: 3.2, delayStep: 90 },
    { count: 10, radius: 170, r: 4.2, delayStep: 70 },
    { count: 6, radius: 95, r: 5.4, delayStep: 55 }
  ]
  const all = []
  let delay = 120
  rings.forEach((ring) => {
    const offset = (Math.PI * 2) / ring.count
    const start = -Math.PI / 2
    const group = []
    for (let i = 0; i < ring.count; i++) {
      const a = start + offset * i
      const node = {
        x: 600 + Math.cos(a) * ring.radius,
        y: 320 + Math.sin(a) * ring.radius * 0.72,
        r: ring.r,
        delay
      }
      delay += ring.delayStep
      group.push(node)
      all.push(node)
    }
    // 相邻节点连线，形成贯通环
    for (let i = 0; i < group.length; i++) {
      links.value.push({ a: group[i], b: group[(i + 1) % group.length], delay: group[i].delay })
    }
  })
  // 内圈到外圈的辐条，隐喻数据贯通链路
  const inner = all.slice(-6)
  inner.forEach((n, i) => {
    links.value.push({ a: n, b: all[i], delay: 600 + i * 90 })
  })
  nodes.value = all
}

function finish () {
  if (leaving.value) return
  leaving.value = true
  clearTimeout(autoTimer)
  fadeTimer = setTimeout(() => emit('finish'), FADE)
}

onMounted(() => {
  buildStage()
  // 尊重系统的“减少动态效果”偏好
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  autoTimer = setTimeout(finish, reduce ? 800 : DURATION)
})

onBeforeUnmount(() => {
  clearTimeout(autoTimer)
  clearTimeout(fadeTimer)
})
</script>

<style lang="scss" scoped>
.splash {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: radial-gradient(120% 120% at 50% 20%, #10233d 0%, #06121f 55%, #030810 100%);
  opacity: 1;
  transition: opacity 0.7s ease, transform 0.7s ease;

  &.is-leaving {
    opacity: 0;
    transform: scale(1.03);
    pointer-events: none;
  }
}

.stage {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.glow {
  animation: breathe 6s ease-in-out infinite;
}

.link {
  stroke-dasharray: 10 26;
  animation: flow 2.6s linear infinite;
}

.node {
  opacity: 0;
  animation: pop 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.content {
  position: relative;
  z-index: 2;
  max-width: 860px;
  padding: 0 24px;
  text-align: center;
  color: #f5f7fa;
}

.title {
  margin: 0;
  font-size: clamp(30px, 5.2vw, 62px);
  font-weight: 600;
  letter-spacing: 0.16em;
  background: linear-gradient(180deg, #ffffff 0%, #a9c7e8 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: rise 1s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.eyebrow {
  margin: 18px 0 0;
  font-size: clamp(12px, 1.4vw, 15px);
  letter-spacing: 0.34em;
  color: #7fa6cf;
  text-transform: uppercase;
  animation: rise 1s cubic-bezier(0.22, 1, 0.36, 1) 0.18s both;
}

.theme {
  margin: 34px auto 0;
  max-width: 640px;
  font-size: clamp(14px, 1.7vw, 19px);
  line-height: 2.1;
  letter-spacing: 0.08em;
  color: #d3e2f2;
  animation: rise 1s cubic-bezier(0.22, 1, 0.36, 1) 0.4s both;
}

.progress {
  width: min(320px, 60vw);
  height: 2px;
  margin: 46px auto 0;
  border-radius: 2px;
  background: rgba(148, 184, 222, 0.22);
  overflow: hidden;

  i {
    display: block;
    height: 100%;
    width: 0;
    border-radius: 2px;
    background: linear-gradient(90deg, #2997ff, #7bdcff);
    animation: grow 4s linear forwards;
  }
}

.skip {
  position: absolute;
  right: max(24px, 4vw);
  bottom: max(24px, 4vh);
  z-index: 3;
  padding: 9px 22px;
  font-size: 13px;
  letter-spacing: 0.12em;
  color: #cfe0f2;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(160, 196, 232, 0.28);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease;

  &:hover {
    color: #ffffff;
    background: rgba(41, 151, 255, 0.22);
    border-color: rgba(123, 220, 255, 0.6);
  }
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(22px);
    filter: blur(6px);
  }
  to {
    opacity: 1;
    transform: none;
    filter: none;
  }
}

@keyframes pop {
  from {
    opacity: 0;
    transform: scale(0.4);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes flow {
  from {
    stroke-dashoffset: 72;
  }
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes grow {
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
}

@keyframes breathe {
  0%, 100% {
    opacity: 0.55;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

// 系统开启“减少动态效果”时不播放循环动画
@media (prefers-reduced-motion: reduce) {
  .glow,
  .link,
  .node,
  .title,
  .eyebrow,
  .theme,
  .progress i {
    animation-duration: 0.01ms;
    animation-iteration-count: 1;
  }
  .node,
  .title,
  .eyebrow,
  .theme {
    opacity: 1;
  }
}
</style>
